import { useMemo } from 'react';
import axios from 'axios';
import type { AppProps } from 'next/app';
import { I18nProvider } from 'next-localization';

import {
  AuthReadyProvider,
  BasketProvider,
  FeaturesContext,
  isBlueParrottHostname,
  isNotNil,
  JabraThemesListContext,
  SiteSettingsContext,
  TrackingProvider,
} from '@gn-audio/jss-components';
import DatadogRum, { DatadogRumConfig } from '@gn-audio/jss-components/components/DatadogRum';
import { getLocaleWithRegion, SiteContext } from '@gn-audio/jss-core';

import { FormatContext } from 'common/contexts/FormatContext';
import useAppBootstrap from 'common/hooks/useAppBootstrap';
import { DualSiteProps, JabraPageProps } from 'common/pages/pagesTypes';
import { CoveoAnalyticsProvider } from 'components/Search/providers/CoveoAnalyticsProvider';

import ErrorPage from './_error';

import 'assets/app.css';
import './../../styles/globals.css';

axios.defaults.timeout = 30000;

function App({
  Component,
  pageProps,
}: AppProps<JabraPageProps | DualSiteProps>): React.JSX.Element {
  const propsForProviders: JabraPageProps = useMemo(() => {
    const isDualPropsSite = 'jabraProps' in pageProps && 'blueparrottProps' in pageProps;
    if (isDualPropsSite) {
      const isBlueparrott =
        typeof window !== 'undefined' &&
        isBlueParrottHostname(window.location.hostname, window.location.search);

      return isBlueparrott ? pageProps.blueparrottProps : pageProps.jabraProps;
    }
    return pageProps;
  }, [pageProps]);

  const {
    envConfig,
    jabraDictionary,
    locale,
    error: serverError,
    themes,
    siteSettings,
    features,
    siteContext,
  } = propsForProviders;

  const { coveoUserAnalyticsAccessToken, coveoUserAnalyticsEndpoint } = envConfig || {};

  const language = getLocaleWithRegion(locale);
  const { formatSettings, authReady } = useAppBootstrap({
    envConfig,
    siteSettings,
    language,
    siteContext: siteContext ?? SiteContext.Jabra,
  });

  if (serverError) {
    console.error(serverError);
    return <ErrorPage />;
  }

  const validThemes = (themes ?? []).filter(isNotNil);

  const datadogRumConfig: DatadogRumConfig = {
    isEnabled: envConfig?.datadogRumEnabled,
    applicationId: envConfig?.datadogRumApplicationId,
    clientToken: envConfig?.datadogRumClientToken,
    site: envConfig?.datadogRumSite,
    serviceName: envConfig?.datadogRumService,
    env: envConfig?.datadogRumEnv,
    version: envConfig?.datadogRumVersion,
    sessionSampleRate: envConfig?.datadogRumSessionSampleRate,
    sessionReplaySampleRate: envConfig?.datadogRumSessionReplaySampleRate,
  };

  return (
    <>
      <DatadogRum config={datadogRumConfig} defaultServiceName="njord-store-frontend" />
      <AuthReadyProvider authReady={authReady}>
        <TrackingProvider>
          <FormatContext.Provider value={formatSettings}>
            <SiteSettingsContext.Provider value={siteSettings ?? {}}>
              <JabraThemesListContext.Provider value={validThemes}>
                <FeaturesContext.Provider value={features}>
                  <I18nProvider lngDict={jabraDictionary} locale={propsForProviders.locale}>
                    <BasketProvider>
                      <CoveoAnalyticsProvider
                        apiKey={coveoUserAnalyticsAccessToken}
                        endpoint={coveoUserAnalyticsEndpoint}
                      >
                        <Component {...pageProps} locale={locale} />
                      </CoveoAnalyticsProvider>
                    </BasketProvider>
                  </I18nProvider>
                </FeaturesContext.Provider>
              </JabraThemesListContext.Provider>
            </SiteSettingsContext.Provider>
          </FormatContext.Provider>
        </TrackingProvider>
      </AuthReadyProvider>
    </>
  );
}

export default App;
